<template lang="pug">
  .multi-file-selecter(:style="{ width: width, maxWidth: width, minHeight: height, maxHeight: height }")
    .d-flex.flex-row.align-items-stretch
      .drag-area(
        @dragleave.prevent="onPrevent()"
        @dragover.prevent="onPrevent()"
        @drop.prevent="dropFile($event)"
        :style="{ minHeight: height, maxHeight: height }")
        .drag-message ドラッグ＆ドロップ

      .select-area
        .file-select
          label.btn.control-btn ファイルを選択
            // @click="(e) => { e.target.value = '' }"は同一ファイルを読込んだ際にonChange()が発火しない問題の対応
            input(ref="file" @change="selectFile($event)" type="file" :accept="_accept" multiple="multiple" @click="(e) => { e.target.value = '' }")
        .file-preview(v-if="showPreview")
          button.btn.preview-btn(v-if="enablePreviewStatus" @click="onPreview()" type="button") プレビュー
          button.btn.preview-btn.disable(v-else type="button" disable) プレビュー
</template>

<script lang="ts">
import { Component, Vue, Prop } from "vue-property-decorator";

/**
 * ### 複数ファイル選択コンポーネント
 *
 * - ファイル自体はこのコンポーネントでは保持しない
 * - 入力したファイルを読み込んで親コンポーネントに渡す
 */
@Component
export default class MultiFileSelecter extends Vue {
  /** コンポーネント横幅 */
  @Prop({
    default: () => {
      return "600px";
    },
    type: String
  })
  width!: string;

  /** コンポーネント縦幅 */
  @Prop({
    default: () => {
      return "70px";
    },
    type: String
  })
  height!: string;

  /** ファイルのmimeType */
  @Prop({ required: true, type: Array })
  mimetypes!: Array<string>;

  /** プレビューボタンの表示可否 */
  @Prop({ required: true, type: Boolean })
  showPreview!: boolean;

  /** プレビューボタンの状態(活性：true、非活性：false) */
  @Prop({ type: Boolean })
  enablePreviewStatus!: boolean;

  /**
   * vue lifecycle created
   */
  public created() {
    return;
  }

  /**
   * ファイルドロップイベント処理
   *
   * 選択されたファイルオブジェクトを親コンポーネントに渡す
   */
  public dropFile(event: DragEvent): void {
    if (!event.dataTransfer) {
      return;
    }
    const files: FileList = event.dataTransfer.files;

    if (0 < files.length) {
      this.propagationFile(files);
    }

    return;
  }

  /**
   * ファイル選択イベント処理
   *
   * ファイル選択ダイアログがOKでもキャンセルでも呼び出される
   * 選択されたファイルオブジェクトを親コンポーネントに渡す
   */
  public selectFile(event: Event): void {
    if (!event.target) {
      return;
    }
    // targetの型定義にFileがいないので、anyにしてからFileを取り出す
    const target: any = event.target;
    const files: FileList = target.files;

    if (0 < files.length) {
      this.propagationFile(files);
    }

    this.clearFileElement();

    return;
  }

  /**
   * file要素をクリアする
   */
  public clearFileElement() {
    // ブラウザの仕様により、同じファイルをファイル選択ダイアログで選択しても、ファイル選択イベントが発生しない
    // これを解決するため、file要素のvalue値を削除のタイミングで空にする
    const file: Vue | Element | Vue[] | Element[] = this.$refs.file;
    if (file instanceof Element) {
      let fileElement: any = file;
      fileElement.value = "";
    }

    return;
  }

  /**
   * ### ファイルオブジェクトを親コンポーネントに渡す
   *
   * - FileListオブジェクトはArray型のメソッドが使えなくて不便なので格納しなおす
   *
   * @param files 選択されたファイルリスト
   */
  public propagationFile(fileList: FileList): void {
    let files: Array<File> = [];

    for (let i = 0; i < fileList.length; i++) {
      files.push(fileList[i]);
    }

    this.$emit("input", files);

    return;
  }

  /**
   * ### プレビューボタンが押下されたことを親コンポーネントに通知する
   */
  public onPreview(): void {
    this.$emit("preview");

    return;
  }

  /**
   * ### acceptで指定出来る形式でmimeytypeを返す
   *
   * - mimetypeが複数ある場合は,区切りにする
   */
  get _accept(): string {
    return this.mimetypes ? this.mimetypes.join(",") : "";
  }

  /**
   * ブラウザのイベント処理回避用
   * 処理は無し
   */
  public onPrevent(): void {
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/pc/_colors.scss";

.multi-file-selecter {
  .drag-area {
    flex: 1;
    border: 2px dashed $form-border-color;
    color: $file-dragarea-font;
    background-color: $file-dragarea;
    border-radius: 0.25rem;
    // ドラッグエリア内のメッセージを縦横中央に配置する
    display: flex;
    align-items: center;
    justify-content: center;
    // marginは全体で使われている8pxに合わせる
    margin-right: 8px;
  }

  .select-area {
    width: 124px;
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;

    .file-comment {
      color: $border-darkgray;
      span {
        font-size: 16px !important;
      }
    }

    .file-select {
      margin-bottom: 8px;
    }

    .control-btn,
    .preview-btn {
      font-size: 14px !important;
      min-width: 124px;
      margin: 0;
      height: 32px !important;
      padding: 4px 4px !important;
      input {
        // ブラウザの標準fileinputを使うのではなく、自作したいので非表示にする
        display: none;
      }
    }
    .control-btn {
      cursor: pointer;
    }
  }
}
</style>
